import { IS_AWS_ENV } from "constants/app.constants";
import {
  HZ_WITH_PRODUCTION_FILTER_NAME,
  SYSTEM_DEFAULT_FILTER_NAME
} from "constants/filter.constants";

import { FilterScope } from "models/filter";

/**
 * An empty filter body, spelled out rather than left as "" or "{}".
 *
 * Not "": that is what an unsaved filter carries, and it is falsy, so the
 * modified check in useSavedFiltersController bails before comparing and an
 * emptied System Default would stop reporting edits made on top of it.
 *
 * Not "{}" either. Selecting a filter runs setFilterState(JSON.parse(body))
 * straight into the reducer, which assigns propertiesFilter through without a
 * default (its neighbours all carry one), so a body missing the key lands
 * undefined in the store and the legend throws on propertiesFilter.flatMap.
 * Every list a reader may walk is therefore present and empty.
 *
 * polygonFilter is the one key deliberately left out. The modified check
 * rebuilds the live snapshot with a polygon only when it has a source, so the
 * key is absent there when no polygon is drawn; spelling it as null here would
 * compare null against undefined and report an untouched filter as modified.
 */
const EMPTY_FILTER_JSON = JSON.stringify({
  propertiesFilter: [],
  wellListFilter: [],
  excludePolygonsFilter: [],
  checkedItems: []
});

type SavedFilterLike = { name: string; filterJSON: string; filterScope?: string };

/**
 * Whether this row is one of the filters the system ships, as opposed to one a
 * user happened to give the same name.
 *
 * Name alone is not enough where the response spans scopes: loadSavedFilters
 * asks for filterScope and, called without a project, comes back with System,
 * Org, Team and Personal rows, so a personal filter called "HZ with Production"
 * would vanish and one called "System Default" would have its body swapped.
 * SaveActiveFilter blocks those names in the UI only, so a filter made straight
 * through the GraphQL mutation, or seeded per org, is not covered by that.
 *
 * Scope is only consulted when the response carries it. The group-by and
 * project queries do not ask for the field, and both are already scoped to one
 * project, so there the name is all we have and all we need.
 */
function isSystemScoped(filter: SavedFilterLike): boolean {
  return filter.filterScope === undefined || filter.filterScope === FilterScope.System;
}

/**
 * EVA-257. The AWS EVA reads wells mapped out of the UDM, whose Status and
 * friends do not carry the values the shipped system filters were written
 * against, so those filters match nothing and the user lands on an empty map.
 * Handling 6m wells unfiltered is acceptable as a starting point, so AWS opens
 * with no filter applied: System Default survives, and stays the selected
 * filter, but holds no elements; HZ with Production is gone.
 *
 * Applied where the list arrives from the backend rather than at each place it
 * is read, so every consumer of the redux list sees the AWS shape already.
 * The filters still exist server-side; this is what the AWS build shows.
 * Everywhere else the list is handed back untouched.
 */
export function applyAwsSystemFilterPolicy<T extends SavedFilterLike>(
  filters: Maybe<T[]>
): Maybe<T[]> {
  if (!IS_AWS_ENV || !Array.isArray(filters)) {
    return filters;
  }

  return filters
    .filter(
      (filter) =>
        !(
          filter?.name?.toLowerCase() === HZ_WITH_PRODUCTION_FILTER_NAME &&
          isSystemScoped(filter)
        )
    )
    .map((filter) =>
      filter?.name?.toLowerCase() === SYSTEM_DEFAULT_FILTER_NAME && isSystemScoped(filter)
        ? { ...filter, filterJSON: EMPTY_FILTER_JSON }
        : filter
    );
}
